import React, { useRef, useState } from 'react'
import {
  Alert, Avatar, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Stack, Step, StepLabel, Stepper, Table,
  TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow,
  Typography, useMediaQuery,
} from '@mui/material'
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ImageNotSupportedOutlinedIcon from '@mui/icons-material/ImageNotSupportedOutlined'
import { saveAs } from 'file-saver'
import { bulkProductMarkups } from '../../services/customer'

export const CSV_TEMPLATE = 'ITEM NUMBER,MARKUP,MARKUP TYPE\r\n'
const actionLabels = {
  new: 'New override', updated: 'Existing override—will update',
  reactivated: 'Will reactivate', unchanged: 'Existing override—unchanged', invalid: 'Needs correction',
}
const countLabels = { new: 'New', updated: 'Updated', reactivated: 'Reactivated', unchanged: 'Unchanged', invalid: 'Invalid' }
const rate = (override) => !override ? 'None' : override.adjustment_type === 'percentage'
  ? `${Number(override.adjustment_value)}%`
  : `$${Number(override.adjustment_value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 })} per item`

export default function BulkProductMarkupUpload({ customerId, token, onChanged }) {
  const [open, setOpen] = useState(false)
  const [file, setFile] = useState(null)
  const [preview, setPreview] = useState(null)
  const [busy, setBusy] = useState('')
  const [message, setMessage] = useState('')
  const [success, setSuccess] = useState(null)
  const [dragging, setDragging] = useState(false)
  const [page, setPage] = useState(0)
  const requestId = useRef(0)
  const input = useRef(null)
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)')
  const smallScreen = useMediaQuery('(max-width:600px)')

  const close = () => {
    if (busy === 'import') return
    requestId.current++
    setOpen(false)
    setFile(null)
    setPreview(null)
    setMessage('')
    setSuccess(null)
    setBusy('')
    setPage(0)
    setDragging(false)
  }

  const validateFile = async (selected) => {
    if (!selected || busy === 'import') return
    const current = ++requestId.current
    setFile(selected)
    setPreview(null)
    setSuccess(null)
    setMessage('')
    setPage(0)
    setDragging(false)
    if (!/\.csv$/i.test(selected.name) || selected.size > 2 * 1024 * 1024) {
      setMessage('Choose a CSV file that is 2 MB or smaller.')
      setBusy('')
      return
    }
    setBusy('validate')
    const result = await bulkProductMarkups(token, customerId, selected)
    if (requestId.current !== current) return
    setBusy('')
    if (result.failed) {
      setMessage(Object.values(result.errors || {}).flat().join(' ') || result.message)
      return
    }
    setPreview(result)
  }

  const importFile = async () => {
    if (busy || !preview?.valid) return
    setBusy('import')
    setMessage('')
    const result = await bulkProductMarkups(token, customerId, file, preview.fingerprint)
    setBusy('')
    if (result.failed) {
      setMessage(Object.values(result.errors || {}).flat().join(' ') || result.message)
      if (result.rows) { setPreview(result); setPage(0) }
      else setPreview(null)
      return
    }
    setSuccess(result.counts)
    onChanged?.()
  }

  const counts = success || preview?.counts
  return (
    <>
      <Button onClick={() => setOpen(true)} startIcon={<UploadFileOutlinedIcon />} sx={{ minHeight: 44, whiteSpace: 'nowrap' }} variant="outlined">Bulk upload</Button>
      <Dialog
        aria-labelledby="bulk-override-title" fullScreen={smallScreen} fullWidth maxWidth="lg"
        onClose={close} open={open} transitionDuration={reducedMotion ? 0 : 180}
        sx={{ '& .MuiButton-root': { minHeight: 44 }, '@media (prefers-reduced-motion: reduce)': { '& *, & *::before, & *::after': { animation: 'none !important', transition: 'none !important' } } }}
      >
        <DialogTitle id="bulk-override-title">Bulk upload product overrides</DialogTitle>
        <DialogContent dividers aria-busy={Boolean(busy)}>
          <Stack spacing={3}>
            <Stepper activeStep={success ? 3 : busy === 'import' ? 2 : preview ? 1 : 0} alternativeLabel>
              {['Upload', 'Validate & review', 'Import'].map((label) => <Step key={label}><StepLabel>{label}</StepLabel></Step>)}
            </Stepper>
            {!success && <>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography component="h3" fontWeight={700} variant="subtitle1">Prepare your CSV</Typography>
                  <Typography color="text.secondary" variant="body2" sx={{ mb: 1.5 }}>One product per row. PERCENTAGE adds a percentage of the base price; FIXED adds a dollar amount per item.</Typography>
                  <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
                    <Table size="small" aria-label="CSV format example" sx={{ '& th, & td': { px: { xs: 1, sm: 1.5 }, py: 1, fontSize: 13 }, '& th': { bgcolor: 'action.hover', fontWeight: 700 }, '& td': { fontVariantNumeric: 'tabular-nums' }, '& tr:last-child td': { borderBottom: 0 } }}>
                      <TableHead>
                        <TableRow>
                          <TableCell scope="col">ITEM NUMBER</TableCell>
                          <TableCell scope="col" align="right">MARKUP</TableCell>
                          <TableCell scope="col">MARKUP TYPE</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow><TableCell>54092</TableCell><TableCell align="right">15</TableCell><TableCell>PERCENTAGE</TableCell></TableRow>
                        <TableRow><TableCell>54377</TableCell><TableCell align="right">2.50</TableCell><TableCell>FIXED</TableCell></TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Typography color="text.secondary" variant="body2" sx={{ mt: 1 }}>Keep item numbers as text in your spreadsheet to preserve leading zeros. Enter markup as a number without % or $.</Typography>
                  <Button onClick={() => saveAs(new Blob([CSV_TEMPLATE], { type: 'text/csv;charset=utf-8;' }), 'product-markup-template.csv')} startIcon={<DownloadOutlinedIcon />} sx={{ mt: 1 }}>Download CSV template</Button>
                </Box>
                <Box
                  onDragOver={(event) => { event.preventDefault(); if (!busy) setDragging(true) }}
                  onDragLeave={() => setDragging(false)}
                  onDrop={(event) => {
                    event.preventDefault()
                    setDragging(false)
                    if (busy) return
                    if (event.dataTransfer.files.length !== 1) { setMessage('Upload one CSV file at a time.'); return }
                    validateFile(event.dataTransfer.files[0])
                  }}
                  sx={{ flex: 1, minWidth: 0, border: '2px dashed', borderColor: dragging ? 'primary.main' : 'divider', bgcolor: dragging ? 'action.selected' : 'background.default', borderRadius: 2, p: 3, textAlign: 'center', alignContent: 'center', transition: reducedMotion ? 'none' : 'background-color 180ms ease' }}
                >
                  <UploadFileOutlinedIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
                  <Typography fontWeight={600}>Drop your CSV here</Typography>
                  <Typography color="text.secondary" variant="body2">Up to 1,000 rows · 2 MB maximum</Typography>
                  <input aria-label="Upload product overrides CSV" accept=".csv,text/csv" hidden ref={input} type="file" onChange={(event) => { validateFile(event.target.files[0]); event.target.value = '' }} />
                  <Button disabled={Boolean(busy)} onClick={() => input.current?.click()} sx={{ mt: 1.5 }} variant="outlined">{file ? 'Choose another CSV' : 'Browse CSV'}</Button>
                  {file && <Typography variant="body2" sx={{ mt: 1, overflowWrap: 'anywhere' }}>{file.name}</Typography>}
                  {file && message && !preview && !busy && <Button onClick={() => validateFile(file)}>Retry validation</Button>}
                </Box>
              </Stack>
              <Alert severity="info">Every row is checked before saving. Existing overrides will be updated after you confirm. Correct any invalid rows in your CSV and upload it again.</Alert>
            </>}
            <Box role="status" aria-live="polite">
              {busy && <Stack alignItems="center" direction="row" spacing={1.5}>
                <CircularProgress size={24} aria-label={busy === 'validate' ? 'Validating CSV' : 'Importing overrides'} />
                <Typography>{busy === 'validate' ? 'Checking every row against inventory and customer pricing…' : 'Importing overrides. Please keep this window open…'}</Typography>
              </Stack>}
              {success && <Alert icon={<CheckCircleOutlineIcon />} severity="success">Import complete. Customer product overrides have been refreshed.</Alert>}
              {preview && !busy && !success && <Alert severity={preview.valid ? 'success' : 'error'}>{preview.valid ? 'All rows are valid. Review the changes below, then confirm the import.' : `${preview.counts.invalid} invalid row(s). Nothing has been saved.`}</Alert>}
            </Box>
            {message && <Alert severity="warning" role="alert">{message}</Alert>}
            {counts && <Stack direction="row" flexWrap="wrap" gap={1}>{Object.entries(countLabels).map(([key, label]) => <Chip key={key} label={`${label}: ${counts[key]}`} variant="outlined" color={key === 'invalid' && counts[key] > 0 ? 'error' : 'default'} />)}</Stack>}
            {preview && !success && <Box>
              <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1, maxHeight: 420 }}>
                <Table stickyHeader size="small" aria-label="CSV validation preview" sx={{ minWidth: 850, '& td': { verticalAlign: 'top', whiteSpace: 'normal', overflowWrap: 'anywhere', py: 1.5 }, '& th': { fontWeight: 700 } }}>
                  <TableHead><TableRow>{['Row', 'Item / product', 'Category', 'Current → proposed', 'Result'].map((label) => <TableCell key={label}>{label}</TableCell>)}</TableRow></TableHead>
                  <TableBody>{preview.rows.slice(page * 25, page * 25 + 25).map((row) => <TableRow key={row.row} sx={{ bgcolor: row.action === 'invalid' ? 'action.hover' : row.current_override ? 'action.selected' : undefined }}>
                    <TableCell>{row.row}</TableCell>
                    <TableCell sx={{ maxWidth: 240 }}><Stack direction="row" spacing={1}>
                      <Avatar alt={row.product?.name || ''} src={row.product?.image || undefined} variant="rounded"><ImageNotSupportedOutlinedIcon fontSize="small" /></Avatar>
                      <Box><Typography variant="body2" fontWeight={700}>{row.item_number || 'Missing item number'}</Typography><Typography variant="body2">{row.product?.name || 'No unique inventory match'}</Typography></Box>
                    </Stack></TableCell>
                    <TableCell>{row.product?.category && row.product.category.toLowerCase() !== 'search' ? row.product.category : 'N/A'}</TableCell>
                    <TableCell><Typography variant="body2">{rate(row.current_override)} → <strong>{row.adjustment_type && row.adjustment_value !== null ? rate(row) : `${row.markup} ${row.markup_type}`}</strong></Typography>{row.current_override && <Typography variant="caption">{row.current_override.active ? 'Existing configuration' : 'Removed configuration'}</Typography>}</TableCell>
                    <TableCell sx={{ minWidth: 240 }}><Typography variant="body2" fontWeight={700} color={row.action === 'invalid' ? 'error.main' : 'text.primary'}>{actionLabels[row.action]}</Typography>{row.errors.map((error) => <Typography key={error} color="error.main" variant="body2">{error}</Typography>)}</TableCell>
                  </TableRow>)}</TableBody>
                </Table>
              </TableContainer>
              <TablePagination component="div" count={preview.rows.length} page={page} onPageChange={(_, value) => setPage(value)} rowsPerPage={25} rowsPerPageOptions={[25]} />
            </Box>}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2, flexWrap: 'wrap', gap: 1 }}>
          <Button disabled={busy === 'import'} onClick={close}>{success ? 'Done' : 'Cancel'}</Button>
          {!success && <Button disabled={Boolean(busy) || !preview?.valid} onClick={importFile} variant="contained">{busy === 'import' ? 'Importing…' : 'Import overrides'}</Button>}
        </DialogActions>
      </Dialog>
    </>
  )
}
